import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
  formatDate,
  getFlagPath,
  getPostPath,
  FLAG_PATH_WORLD_ICON,
} from '../../Functions/Utilities'
import { useLanguage } from '../../Context/LanguageContext'

const isRTL = (nationality) => {
  if (!nationality || !nationality.data || !nationality.data.attributes) return false
  if (typeof nationality.data.attributes.is_rtl === 'boolean') {
    return nationality.data.attributes.is_rtl
  }
  const rtlLangs = ['ar', 'fa', 'he', 'ur', 'ps', 'dv', 'ku', 'yi']
  const lang = nationality.data.attributes.language_query?.toLowerCase()
  return rtlLangs.includes(lang)
}

const PostCard = ({ post, postType }) => {
  const { getLocalizedPath } = useLanguage()
  const [flagImageFailed, setFlagImageFailed] = useState(false)
  const { id, attributes } = post
  const {
    title,
    description,
    createdAt,
    picture,
    post_type,
    start,
    is_imigo_partner,
    nationality,
  } = attributes

  const isRtl = isRTL(nationality)
  const flagPath = getFlagPath(nationality)
  const showWorldFlag = flagPath === FLAG_PATH_WORLD_ICON
  const type = postType || (post_type?.data?.attributes?.name || 'Article')

  useEffect(() => {
    setFlagImageFailed(false)
  }, [flagPath])

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ''
  }

  const getImageUrl = () => {
    if (picture?.data?.attributes?.formats?.medium?.url) {
      return `https://panel.imigo.app${picture.data.attributes.formats.medium.url}`
    }
    if (picture?.data?.attributes?.url) {
      return `https://panel.imigo.app${picture.data.attributes.url}`
    }
    return 'https://panel.imigo.app/uploads/medium_logo_baf7005f31.jpg'
  }

  const handleImageError = (e) => {
    e.target.src = 'https://panel.imigo.app/uploads/medium_logo_baf7005f31.jpg'
  }

  const postHref = getLocalizedPath(
    getPostPath({
      ...post,
      attributes: {
        ...attributes,
        slug:
          attributes.slug ||
          (title
            ? title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')
            : id),
        post_type:
          post_type?.data?.attributes?.name != null
            ? post_type
            : postType
              ? { data: { attributes: { name: postType } } }
              : { data: { attributes: { name: 'Article' } } },
      },
    })
  )

  const getShortDescription = () => {
    const strippedText = stripHtml(description)
    return strippedText.length > 140 ? `${strippedText.substring(0, 140)}…` : strippedText
  }

  return (
    <article
      className={`imigo-post-card group ${isRtl ? 'imigo-post-card--rtl' : ''}`}
    >
      <Link to={postHref} className="imigo-post-card__media-link">
        <div className="imigo-post-card__media">
          <img
            src={getImageUrl()}
            alt=""
            className="imigo-post-card__img"
            onError={handleImageError}
          />
          <div className="imigo-post-card__media-overlay" aria-hidden />
          <div className="imigo-post-card__badges">
            {type && (
              <span className="imigo-post-card__chip imigo-post-card__chip--type">{type}</span>
            )}
            {is_imigo_partner && (
              <span className="imigo-post-card__chip imigo-post-card__chip--partner">Partner</span>
            )}
          </div>
          {flagPath && (
            <div
              className="imigo-post-card__flag"
              title={showWorldFlag ? 'International / English' : 'Nationality / language'}
            >
              {showWorldFlag || flagImageFailed ? (
                <span className="imigo-post-card__flag-fallback" aria-hidden="true">
                  <i className="fas fa-globe" />
                </span>
              ) : (
                <img
                  src={flagPath}
                  alt=""
                  className="imigo-post-card__flag-img"
                  onError={() => setFlagImageFailed(true)}
                />
              )}
            </div>
          )}
        </div>
      </Link>
      <div className="imigo-post-card__body">
        <Link to={postHref} className="imigo-post-card__title-link">
          <h3 className="imigo-post-card__title">{title}</h3>
        </Link>
        <p className="imigo-post-card__excerpt">{getShortDescription()}</p>
        <div className="imigo-post-card__meta">
          <time dateTime={createdAt} className="imigo-post-card__date">
            {formatDate(createdAt)}
          </time>
          {start && (
            <span className="imigo-post-card__event-date">
              {new Date(start).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </article>
  )
}

PostCard.propTypes = {
  post: PropTypes.object.isRequired,
  postType: PropTypes.string,
}

export default PostCard
